import React from 'react';
import { Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HelpInfoCalloutProps {
  title: string;
  children: React.ReactNode;
  /**
   * Optional wrapper class — typically used to apply a codified top-spacing
   * value from SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING (see
   * helpSheetOpticalSpacing.ts and help-sheet-homogenization-principles.md
   * §4.4). The inner structure stays fixed.
   */
  className?: string;
}

/**
 * Canonical help-sheet "info callout" primitive: a 4px-radius pill with
 * the Lightbulb icon, a bolded title, and body copy.
 *
 * The root element is tagged with `data-help-sheet-callout="info"` so
 * regression tests can locate callouts structurally instead of relying on
 * a specific alpha class. See
 * `src/pages/__tests__/ShortTurnHelpSheetAuditPage.test.tsx` for the
 * consumer.
 */
export const HelpInfoCallout: React.FC<HelpInfoCalloutProps> = ({ title, children, className }) => (
  <div
    data-help-sheet-callout="info"
    className={cn(
      'rounded-[0.25rem] border border-primary/10 bg-primary/[0.03] px-2 py-2',
      className,
    )}
  >
    <div className="flex items-start gap-2">
      <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-1 text-primary-text/90" aria-hidden="true" />
      <span className="font-body text-[10px] sm:text-[12px] leading-[1.5] text-foreground/80">
        <strong className="font-medium text-primary-text">{title}:</strong>{' '}
        {children}
      </span>
    </div>
  </div>
);
